import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiGet, apiPost, apiPatch, apiDelete, operationsClient } from '@/lib/api';
import { payrollProfileEndpoints } from '@/lib/api/endpoints';
import { payrollProfileKeys } from '@/lib/queryKeys';
import type {
  CreatePayrollProfileDto,
  PayrollProfile,
  UpdatePayrollProfileDto,
} from '@/types/payrollProfile';

export function usePayrollProfiles(params: { search?: string } = {}) {
  return useApiQuery<PayrollProfile[]>({
    queryKey: payrollProfileKeys.list(params),
    queryFn: () =>
      apiGet<PayrollProfile[]>(operationsClient, payrollProfileEndpoints.list(), {
        params,
      }),
  });
}

export function useCreatePayrollProfile() {
  const queryClient = useQueryClient();
  return useApiMutation<PayrollProfile, CreatePayrollProfileDto>({
    mutationFn: (dto) =>
      apiPost<PayrollProfile, CreatePayrollProfileDto>(
        operationsClient,
        payrollProfileEndpoints.list(),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: payrollProfileKeys.all() });
      toast.success('Payroll profile created');
    },
  });
}

export function useUpdatePayrollProfile() {
  const queryClient = useQueryClient();
  return useApiMutation<PayrollProfile, { id: string; dto: UpdatePayrollProfileDto }>({
    mutationFn: ({ id, dto }) =>
      apiPatch<PayrollProfile, UpdatePayrollProfileDto>(
        operationsClient,
        payrollProfileEndpoints.detail(id),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: payrollProfileKeys.all() });
      toast.success('Payroll profile updated');
    },
  });
}

export function useDeletePayrollProfile() {
  const queryClient = useQueryClient();
  return useApiMutation<unknown, string>({
    mutationFn: (id) => apiDelete(operationsClient, payrollProfileEndpoints.detail(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: payrollProfileKeys.all() });
      toast.success('Payroll profile deleted');
    },
  });
}
